// external libraries
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getISOWeek, parseISO } from 'date-fns';

// types
import type { GridColumnGroupingModel } from '@mui/x-data-grid-pro';

/**
 * The header groupings for both grids: the employee grid groups its date columns per week number,
 * the open-requests grid puts them under one "open requests" header alongside its own columns.
 *
 * `getISOWeek`, not `getWeek`: the dayjs call this replaces followed the nl locale (week starts on
 * Monday, first week contains 4 January), which is the ISO week. Plain `getWeek` defaults to Sunday
 * and firstWeekContainsDate 1, and differs from it on roughly half the year boundaries.
 *
 * It also drops a latent crash: `dayjs(date).week()` needs the `weekOfYear` plugin, which nothing
 * in this folder registers — it happened to work only because another screen imports it and pulls
 * RosterPlan in transitively.
 */
const useRosterColumnGroupings = (datesToDisplay: string[]) => {
  const { t } = useTranslation('common', { keyPrefix: 'rosterPlan' });

  return useMemo(() => {
    const weekGroupings = datesToDisplay.reduce<
      Record<string, { groupId: string; children: { field: string }[] }>
    >((groups, date) => {
      const groupId = t('dataGrid.weekNumber', {
        weekNumber: getISOWeek(parseISO(date))
      });

      groups[groupId] ??= { groupId, children: [] };
      groups[groupId].children.push({ field: date });

      return groups;
    }, {});

    const employeeColumnGrouping: GridColumnGroupingModel =
      Object.values(weekGroupings);

    const openRequestsColumnGrouping: GridColumnGroupingModel = [
      {
        groupId: 'functionName',
        headerName: t('dataGrid.functions'),
        children: [{ field: 'functionName' }]
      },
      {
        groupId: 'dates',
        headerName: t('dataGrid.openRequests'),
        children: datesToDisplay.map((date) => ({ field: date }))
      },
      {
        groupId: 'commitmentHours',
        headerName: t('dataGrid.commitmentHours'),
        children: [{ field: 'commitmentHours' }]
      },
      {
        groupId: 'metrics',
        headerName: ' ',
        children: [
          { field: 'balance' },
          { field: 'cumulative' },
          { field: 'worked' },
          { field: 'leave' },
          { field: 'year' },
          { field: 'plb' },
          { field: 'plbYear' }
        ]
      }
    ];

    return { employeeColumnGrouping, openRequestsColumnGrouping };
  }, [datesToDisplay, t]);
};

export default useRosterColumnGroupings;
